import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useStoreState, Session } from "ezStore";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";
import { Button, FormGroup, Table, Tag, TagSize, Textarea } from "@cofano/cck";

import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import TableViewIcon from "@mui/icons-material/TableView";
import ViewListIcon from "@mui/icons-material/ViewList";
import CircleIcon from "@mui/icons-material/Circle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { useConfirm } from "components/hooks/useConfirm";
import FormField from "components/cck/FormField";
import DatePicker from "components/cck/DatePicker";
import { CustomDialog } from "components/dialogs/Dialogs";

import { DataFormat, usePermission } from "utils";
import { FormattedDate } from "utils/DateUtils";

import { getActualStatusLabel, getEstimatedStatusLabel, isActualTimeRequired, shouldShowActualTime, StatusHistoryView } from "view/statuses/utils/StatusUtils";
import { StatusCallEntity, StatusCargoEntity, StatusVoyageEntity } from "view/statuses/types/status";

import { StatusChangeFragmentFragment as StatusChangeFragment } from "__generated__/view/statuses/graphql/fragments.generated";
import {
	DeleteStatusChangeDocument,
	DeleteStatusChangeMutation,
	SaveStatusChangeDocument,
	SaveStatusChangeMutation,
} from "__generated__/view/statuses/graphql/mutations.generated";
import { GetStatusChangesDocument } from "__generated__/view/statuses/graphql/queries.generated";
import { StatusType } from "__generated__/enums";

type HistoryStatusChangesProps = {
	refetch: () => void;
	entity: StatusVoyageEntity | StatusCallEntity | StatusCargoEntity;
}

const HistoryStatusChanges = ({ refetch, entity }: HistoryStatusChangesProps) => {

	const [ openIndex, setOpenIndex ] = useState<number | undefined>(0);
	const [ selectedStatusChange, setSelectedStatusChange ] = useState<StatusChangeFragment | undefined>(undefined);
	const [ toggleViewValue, setToggleViewValue ] = useState(StatusHistoryView.Accordion);

	const session = useStoreState(store => store.session);
	const confirm = useConfirm();
	const { isReadOnly, isAdmin, isSkipperOrShipManager, isCustomer } = usePermission();

	const statusType: StatusType = "cargoNumber" in entity ? StatusType.Cargo : "voyageNumber" in entity ? StatusType.Voyage : StatusType.Call;

	const { data: queriedStatusChanges, loading, refetch: refetchStatus } = useQuery(GetStatusChangesDocument, {
		variables: {
			search: {
				cargoIds: entity.id && "cargoNumber" in entity ? [ entity.id ] : undefined,
				// callIds: callId ? [ callId ] : undefined,
				voyageIds: entity.id && "voyageNumber" in entity ? [ entity.id ] : undefined,
				statusSearch: { types: [ statusType ] },
			},
			pagination: { size: 999 } },
		skip: !!entity.statusChanges,
	});

	const refetchData = () => {
		refetch();
		refetchStatus();
	};

	const [ deleteStatusChange, { loading: deleteLoading } ] = useMutation<DeleteStatusChangeMutation>(DeleteStatusChangeDocument, {
		onCompleted: () => refetchData(),
	});

	const effectiveStatusChanges = entity.statusChanges ?? queriedStatusChanges?.statusChanges.content ?? [];
	const sortedStatuses = effectiveStatusChanges.sort((a, b) => (b.actualTime ?? b.timestamp ?? 0) - (a.actualTime ?? a.timestamp ?? 0));

	return (
		<Box p={2} sx={{ width: toggleViewValue === StatusHistoryView.Accordion ? "400px" : "600px" }}>
			<Box
				mb={2}
				sx={{
					alignItems: "center",
					display: "flex",
					justifyContent: "space-between",
				}}>
				<Typography variant="h6" color="textPrimary" sx={{ fontSize: 22 }}>
					{intl.formatMessage({ id: "STATUSES_TITLE" })}
				</Typography>

				<ToggleButtonGroup
					value={toggleViewValue}
					onChange={(_event, nextNiew) => setToggleViewValue(nextNiew)}
					size="small"
					exclusive>
					<ToggleButton value={StatusHistoryView.Table}>
						<TableViewIcon fontSize="small" />
					</ToggleButton>

					<ToggleButton value={StatusHistoryView.Accordion}>
						<ViewListIcon fontSize="small" />
					</ToggleButton>
				</ToggleButtonGroup>
			</Box>

			{loading ? (
				<CircularProgress size={20} />
			) : (
				<React.Fragment>
					{toggleViewValue === StatusHistoryView.Table ? (
						<Table
							id="historyStatusChanges"
							defaultSize="SPACIOUS"
							content={sortedStatuses}
							contentLoading={loading}
							columns={[ {
								id: "color",
								title: "",
								render: (row: StatusChangeFragment) => <CircleIcon fontSize="small" htmlColor={row.status.color} />,
								width: 40,
								disableFlexGrow: true,
							}, {
								id: "status",
								title: intl.formatMessage({ id: "STATUS" }),
								render: (row: StatusChangeFragment) => DataFormat.status(row.status, session.locale),
							}, {
								id: "id",
								title: intl.formatMessage({ id: "COMMENT" }),
								render: (row: StatusChangeFragment) => row.comment,
							}, {
								id: "timestamp",
								title: intl.formatMessage({ id: "TIMESTAMP" }),
								render: (row: StatusChangeFragment) => <FormattedDate date={row.timestamp} withoutYear />,
								width: 120,
								disableFlexGrow: true,
							} ]}
							rowActions={isReadOnly ? undefined : [ {
								icon: <EditIcon />,
								onClick: (row: StatusChangeFragment) => setSelectedStatusChange(row),
							}, {
								icon: <DeleteIcon />,
								hidden: (row: StatusChangeFragment) => !(isAdmin || row.deletable),
								onClick: (row: StatusChangeFragment) => {
									confirm({
										type: "DELETE",
									}).then(() => {
										deleteStatusChange({
											variables: { id: row.id },
										});
									});
								},
							} ]}
							disableFooter />
					) : (
						<React.Fragment>
							{sortedStatuses.map((statusChange, index: number) => (
								<Accordion
									style={{ borderLeft: `3px solid ${statusChange.status.color}` }}
									expanded={openIndex === index}
									onChange={() => setOpenIndex(openIndex === index ? undefined : index)}>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
											<Typography>
												{DataFormat.status(statusChange.status, session.locale)}
											</Typography>
											{statusChange.communicatedToCustomer != null && (
												<LibraryAddCheckIcon fontSize="small" htmlColor={`${statusChange.status.color}`} />
											)}
										</Box>
									</AccordionSummary>

									<AccordionDetails>
										<Typography>
											{statusChange.actualTime && (
												<FormGroup label={getActualStatusLabel(statusChange.status.requireActualTime)} overflow="hidden">
													<FormattedDate date={statusChange.actualTime} withoutYear />
												</FormGroup>
											)}

											{statusChange.estimateTime && (
												<FormGroup label={getEstimatedStatusLabel(statusChange.status.estimateType)} overflow="hidden">
													<FormattedDate date={statusChange.estimateTime} withoutYear />
												</FormGroup>
											)}

											{statusChange.status.allowComment && !isSkipperOrShipManager && (
												<FormGroup label={intl.formatMessage({ id: "COMMENT" })} overflow="hidden">
													<span>{statusChange.comment || "-"}</span>
												</FormGroup>
											)}

											{statusChange.status.allowSkipperComment && (
												<FormGroup label={intl.formatMessage({ id: "SKIPPER_COMMENT" })} overflow="hidden">
													<span>{statusChange.skipperComment || "-"}</span>
												</FormGroup>
											)}

											{statusChange.communicatedToCustomer && (
												<FormGroup label={intl.formatMessage({ id: "COMMUNICATED_TO_CUSTOMER" })} overflow="hidden">
													<FormattedDate date={statusChange.communicatedToCustomer} withoutYear />
												</FormGroup>
											)}

											<Box
												sx={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "flex-end",
													gap: 1,
													flexWrap: "wrap",
												}}
											>
												<Box sx={{ maxWidth: "100%" }}>
													<FormGroup label={intl.formatMessage({ id: "APPLIED_BY" })} overflow="hidden">
														<span
															style={{
																display: "flex",
																flexDirection: "column",
																rowGap: 4,
															}}
														>
															<div>
																<small style={{ textOverflow: "ellipsis", overflow: "hidden" }}>
																	{statusChange.appliedBy ?? intl.formatMessage({ id: "UNKNOWN" })}{" - "}
																</small>
																<small>
																	<FormattedDate date={statusChange.timestamp} withoutYear />
																</small>
															</div>
															{statusChange.origin && (
																<>
																	<small>
																		{intl.formatMessage({ id: statusChange.origin.status.type })}
																		{" - "}
																		<Tag
																			color={statusChange.origin.status.color}
																			size={TagSize.SMALL}
																			text={DataFormat.status(statusChange.origin.status, undefined)}
																		/>
																	</small>
																</>
															)}
														</span>
													</FormGroup>
												</Box>

												{!isReadOnly && !isCustomer && (
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														<Button
															onClick={() => setSelectedStatusChange(statusChange)}
															loading={deleteLoading}
															variant="outlined"
															size="small">
															{intl.formatMessage({ id: "EDIT" })}
														</Button>
														{(isAdmin || statusChange.deletable) && (
															<Button
																onClick={() => {
																	confirm({ type: "DELETE" }).then(() => {
																		deleteStatusChange({
																			variables: { id: statusChange.id },
																		});
																	});
																}}
																loading={deleteLoading}
																color="danger"
																size="small"
															>
																{intl.formatMessage({ id: "DELETE" })}
															</Button>
														)}
													</Box>
												)}
											</Box>
										</Typography>
									</AccordionDetails>
								</Accordion>
							))}
						</React.Fragment>
					)}
				</React.Fragment>
			)}

			{selectedStatusChange && (
				<StatusChangeEditDialog
					statusChange={selectedStatusChange}
					open={true}
					onClose={() => {
						setSelectedStatusChange(undefined);
					}}
					refetch={refetchData}
					session={session} />
			)}
		</Box>
	);
};

type StatusChangeEditDialogProps = {
	statusChange: StatusChangeFragment;
	open: boolean;
	onClose: () => void;
	session: Session;
	refetch: () => void;
}

type StatusChangeEditDialogForm = {
	actualTime?: number;
	comment?: string;
	skipperComment?: string;
}

const StatusChangeEditDialog = ({ statusChange, open, onClose, session, refetch }: StatusChangeEditDialogProps) => {
	const { control, handleSubmit, reset, formState } = useForm<StatusChangeEditDialogForm>({
		defaultValues: {
			actualTime: statusChange.actualTime ?? undefined,
			comment: statusChange.comment ?? "",
			skipperComment: statusChange.skipperComment ?? "",
		},
	});

	const [ saveStatus, { loading } ] = useMutation<SaveStatusChangeMutation>(SaveStatusChangeDocument, {
		onCompleted: () => {
			onClose();
			reset();
			refetch();
		},
	});

	const { isSkipperOrShipManager } = usePermission();

	const save = (data: StatusChangeEditDialogForm) => {
		saveStatus({
			variables: {
				input: {
					id: statusChange.id,
					actualTime: data.actualTime,
					comment: data.comment,
					skipperComment: data.skipperComment,
				},
			},
		});
	};

	return (
		<CustomDialog
			open={open}
			title={intl.formatMessage({ id: "STATUSES_EDIT" })}
			maxWidth="xs"
			onClose={onClose}
			actions={(
				<Button
					onClick={handleSubmit(save)}
					loading={loading}
					size="small">
					{intl.formatMessage({ id: "SAVE" })}
				</Button>
			)}>

			{shouldShowActualTime(statusChange.status.requireActualTime) && (
				<FormGroup label={getActualStatusLabel(statusChange.status.requireActualTime)} overflow="hidden" required={isActualTimeRequired(statusChange.status.requireActualTime, session)}>
					<FormField errors={formState.errors} name="actualTime">
						<Controller
							name={"actualTime"}
							control={control}
							rules={{ required: isActualTimeRequired(statusChange.status.requireActualTime, session) }}
							render={({ field }) => <DatePicker {...field} showTimeInput />} />
					</FormField>
				</FormGroup>
			)}

			{!isSkipperOrShipManager && statusChange.status.allowComment && (
				<FormGroup label={intl.formatMessage({ id: "COMMENT" })} overflow="hidden">
					<Controller
						name={"comment"}
						control={control}
						render={({ field }) => <Textarea {...field} />} />
				</FormGroup>
			)}

			{statusChange.status.allowSkipperComment && (
				<FormGroup label={intl.formatMessage({ id: "SKIPPER_COMMENT" })} overflow="hidden">
					<Controller
						name={"skipperComment"}
						control={control}
						render={({ field }) => <Textarea disabled={!isSkipperOrShipManager} {...field} />} />
				</FormGroup>
			)}
		</CustomDialog>
	);

};

export default HistoryStatusChanges;
